import { useEffect, useRef, useState } from 'react'
import { API_ROUTES, LiveMessageSchema, type LiveMessage } from '@brochure/shared'

/** Ogni quanto si controlla che la connessione sia viva, e quanto si aspetta la risposta prima di darla per morta. */
const HEARTBEAT_MS = 20_000
const PONG_TIMEOUT_MS = 10_000

/** Stanza WebSocket del torneo: riconnette con backoff; `onMessage` riceve messaggi già validati, più un
 * messaggio sintetico `tournament` a ogni riconnessione.
 *
 * Una connessione può morire senza chiudersi (scheda congelata in secondo piano, telefono in standby, proxy o
 * server riavviato): il browser la crede aperta e non arriva più niente. Per questo ogni 20 s, e a ogni ritorno
 * sulla scheda, si manda "ping": se "pong" non arriva entro 10 s la connessione si butta e se ne apre una nuova,
 * che rilegge i dati. */
export function useLive(tournamentId: string | undefined, onMessage: (message: LiveMessage) => void): { isOpen: boolean } {
  const [isOpen, setIsOpen] = useState(false)
  const handler = useRef(onMessage)
  handler.current = onMessage
  useEffect(() => {
    if (!tournamentId) return
    let socket: WebSocket | null = null
    let timer: number | undefined
    let pongTimer: number | undefined
    let delay = 1000
    let stopped = false
    // La prima apertura non emette il messaggio sintetico: al montaggio il chiamante ha già fatto la sua GET.
    let hasOpened = false

    const scheduleReconnect = () => {
      setIsOpen(false)
      if (stopped) return
      window.clearTimeout(timer)
      timer = window.setTimeout(connect, delay)
      delay = Math.min(delay * 2, 10_000)
    }
    // Connessione morta: si stacca (i suoi eventi non contano più) e se ne apre subito un'altra.
    const drop = () => {
      window.clearTimeout(pongTimer)
      if (socket) {
        socket.onopen = socket.onmessage = socket.onclose = socket.onerror = null
        socket.close()
        socket = null
      }
      delay = 1000
      scheduleReconnect()
    }
    const ping = () => {
      if (socket?.readyState !== WebSocket.OPEN) return
      window.clearTimeout(pongTimer)
      socket.send('ping')
      pongTimer = window.setTimeout(drop, PONG_TIMEOUT_MS)
    }

    function connect() {
      const protocol = location.protocol === 'https:' ? 'wss' : 'ws'
      const current = new WebSocket(`${protocol}://${location.host}${API_ROUTES.ws}?tournamentId=${encodeURIComponent(tournamentId!)}`)
      socket = current
      current.onopen = () => {
        setIsOpen(true)
        delay = 1000
        // Riconnessione: i messaggi persi mentre era giù vanno recuperati con una rilettura.
        if (hasOpened) handler.current({ type: 'tournament', tournamentId: tournamentId!, at: new Date().toISOString() })
        hasOpened = true
      }
      current.onmessage = (event) => {
        if (event.data === 'pong') return window.clearTimeout(pongTimer)
        let raw: unknown
        try { raw = JSON.parse(String(event.data)) } catch { return } // messaggio malformato: ignorato
        const parsed = LiveMessageSchema.safeParse(raw)
        if (parsed.success) handler.current(parsed.data)
      }
      current.onclose = () => { window.clearTimeout(pongTimer); socket = null; scheduleReconnect() }
      current.onerror = () => current.close()
    }

    // Tornando sulla scheda: se la connessione è caduta si riapre subito, se sembra aperta si verifica.
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || stopped) return
      if (socket) ping()
      else { delay = 1000; window.clearTimeout(timer); connect() }
    }
    const heartbeat = window.setInterval(ping, HEARTBEAT_MS)
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('online', onVisible)
    connect()
    return () => {
      stopped = true
      window.clearTimeout(timer)
      window.clearTimeout(pongTimer)
      window.clearInterval(heartbeat)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('online', onVisible)
      socket?.close()
    }
  }, [tournamentId])
  return { isOpen }
}
